import {
  getAPIGatewayDataFromParent,
  postGenericFromParent,
} from "../../../handler/ParentDataHandler";

export const METRICS_FETCH_START = "METRICS_FETCH_START";
export const METRICS_FETCH_SUCCESS = "METRICS_FETCH_SUCCESS";
export const METRICS_FETCH_ERROR = "METRICS_FETCH_ERROR";

/**
 * Fetch a single metric from the metric endpoint
 * @param {Object} metricDefinitionSpec - Metric definition specification
 * @param {Array} filters - Array of filter objects with name and value properties
 * @returns {Function} Redux thunk function
 */
export function fetchMetric(metricDefinitionSpec, filters) {
  return async (dispatch) => {
    const id = metricDefinitionSpec.id;
    dispatch({ type: METRICS_FETCH_START, metricId: id });

    try {
      const request = buildMetricRequestObject([metricDefinitionSpec], filters);
      const response = request.baseURL
        ? await postGenericFromParent(request.requestBody, request.baseURL)
        : await getAPIGatewayDataFromParent(request);

      response.data.metricResults.forEach((metricData) => {
        dispatch({
          type: METRICS_FETCH_SUCCESS,
          metricId: metricData.metric.metricName,
          payload: metricData,
        });
      });
    } catch (err) {
      dispatch({
        type: METRICS_FETCH_ERROR,
        error: err.message,
        metricId: id,
      });
    }
  };
}

/**
 * Fetch multiple metrics from the metric endpoint. Ths function batches metric calls by endpoint
 * @param {Array} metricDefinitionSpecs - Array of metric definition specifications
 * @param {Array} filters - Array of filter objects with name and value properties
 * @returns {Function} Redux thunk function
 */
export function fetchMetrics(metricDefinitionSpecs, filters) {
  return async (dispatch) => {
    const metricIds = metricDefinitionSpecs.map((spec) => spec.id);
    metricIds.forEach((id) =>
      dispatch({ type: METRICS_FETCH_START, metricId: id }),
    );

    const groupedMetrics = groupMetricsByEndpoint(metricDefinitionSpecs);
    const requests = Object.entries(groupedMetrics).map(
      ([processPath, specs]) => buildMetricRequestObject(specs, filters),
    );

    for (const request of requests) {
      try {
        const response = request.baseURL
          ? await postGenericFromParent(request.requestBody, request.baseURL)
          : await getAPIGatewayDataFromParent(request);
        response.data.metricResults.forEach((metricData) => {
          dispatch({
            type: METRICS_FETCH_SUCCESS,
            metricId: metricData.metric.metricName,
            payload: metricData,
          });
        });
      } catch (err) {
        request.requestBody.metricDataQueries.forEach((query) => {
          dispatch({
            type: METRICS_FETCH_ERROR,
            error: err.message,
            metricId: query.metric.metricName,
          });
        });
      }
    }
  };
}

/**
 * Build request object for metric API call
 * @param {Array} metricSpecs - Array of metric specifications
 * @param {Array} filters - Array of filter objects with name and value properties
 * @returns {Object} Request object with endpoint configuration and request body
 */
function buildMetricRequestObject(metricSpecs, filters) {
  const requestBody = {
    metricDataQueries: metricSpecs.map((spec) => {
      const metricQuery = {
        metric: {
          metricName: spec.id,
          dimensions: filters,
        },
      };

      // Add period if available
      if (spec.period && Array.isArray(spec.period) && spec.period.length > 0) {
        metricQuery.metric.period = spec.period;
      }

      // Add statistic if available (use selectedStat or default to first in stat array)
      if (spec.stat && Array.isArray(spec.stat) && spec.stat.length > 0) {
        const selectedStat = spec.selectedStat || spec.stat[0];
        metricQuery.metric.statistic = selectedStat;
      }

      return metricQuery;
    }),
  };
  return {
    ...metricSpecs[0].configuration.dataSource.endpoint,
    requestBody: requestBody,
  };
}

/**
 * Group metrics by their endpoint processName for batching
 * @param {Array} metricSpecs - Array of metric specifications
 * @returns {Object} Object with processName as keys and arrays of metric specs as values
 */
function groupMetricsByEndpoint(metricSpecs) {
  return metricSpecs.reduce((groups, spec) => {
    const processName = spec.configuration.dataSource.endpoint.processName;
    if (!groups[processName]) {
      groups[processName] = [];
    }
    groups[processName].push(spec);
    return groups;
  }, {});
}



// WEBPACK FOOTER //
// ./src/components/mySCC/common/thunks/MetricDataDispatcher.js